import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Product } from 'app/models/product.models';
import { Variant } from 'app/models/variant.models';
import { ConversionUnits } from 'app/models/conversion-unit.models';
import {
  CompositeArticleVariant,
  CreateCompositeArticleVariantDto,
} from 'app/models/composite-article-variant.models';
import { ArticleTax, CreateArticleTaxDto, UpdateArticleTaxDto } from 'app/models/article-tax.models';

@Injectable({
  providedIn: 'root',
})
export class ProductoService {
  articulos: Observable<Product[] | any>;
  articulo: Observable<Product | any>;

  constructor(private http: HttpClient) {}

  getArticulos(
    pagina: number,
    itemXPag: number,
    search?: string
  ): Observable<Product | any> {
    return this.http.get(
      environment.apiUrl + `articles?pag=${pagina}&itemXPag=${itemXPag}&search=${search}`
    );
  }

  getArticulosConFiltros(
    pagina: number,
    itemXPag: number,
    search?: string,
    rubros?: number[],
    marcas?: number[],
    proveedores?: number[],
    tipoItem?: string
  ): Observable<Product | any> {
    let params = `pag=${pagina}&itemXPag=${itemXPag}`;
    
    if (search && search.trim()) {
      params += `&search=${encodeURIComponent(search.trim())}`;
    }
    
    // Solo agregar parámetros si tienen valores válidos
    if (rubros && Array.isArray(rubros) && rubros.length > 0) {
      const rubrosValidos = rubros.filter(id => id !== null && id !== undefined && !isNaN(id));
      if (rubrosValidos.length > 0) {
        params += `&rubros=${rubrosValidos.join(',')}`;
      }
    }
    
    if (marcas && Array.isArray(marcas) && marcas.length > 0) {
      const marcasValidas = marcas.filter(id => id !== null && id !== undefined && !isNaN(id));
      if (marcasValidas.length > 0) {
        params += `&marcas=${marcasValidas.join(',')}`;
      }
    }
    
    if (proveedores && Array.isArray(proveedores) && proveedores.length > 0) {
      const proveedoresValidos = proveedores.filter(id => id !== null && id !== undefined && !isNaN(id));
      if (proveedoresValidos.length > 0) {
        params += `&proveedores=${proveedoresValidos.join(',')}`;
      }
    }
    
    // Agregar filtro por tipo de item
    if (tipoItem && tipoItem.trim()) {
      params += `&tipos=${encodeURIComponent(tipoItem.trim())}`;
    }
    
    const url = environment.apiUrl + `articles?${params}`;
    console.log('🔗 URL con filtros:', url);
    console.log('📋 Parámetros de filtrado:', {
      rubros: rubros,
      marcas: marcas,
      proveedores: proveedores,
      tipos: tipoItem,
      rubrosLength: rubros ? rubros.length : 0,
      marcasLength: marcas ? marcas.length : 0,
      proveedoresLength: proveedores ? proveedores.length : 0
    });
    
    return this.http.get(url);
  }

  getVariantesDisponibleVenta(): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/sales/avaliability');
  }

  getVariantesTipoInsumos(): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/type/inputs');
  }

  getVariantesDisponiblesCompraProveedor(supplierId: number): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/purchase/availability/' + supplierId);
  }

  getVariantePorCodigoBarras(codigoBarras: string): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/barcode/' + codigoBarras);
  }

  getNuevocodeArticulos(): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'articles/code/new');
  }

  getNuevocodeBarrasArticulos(): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'articles/barcode/new');
  }

  getCantidadArticulosActivos(): Observable<any> {
    return this.http.get(environment.apiUrl + 'articles/cantidad');
  }

  getMediosDePago(): Observable<any> {
    return (this.articulos = this.http.get(environment.apiUrl + 'mediopagos'));
  }

  getMedioDePagoByNombre(nombre: string): Observable<any> {
    return (this.articulos = this.http.get(
      environment.apiUrl + 'mediopago/nombre/' + nombre
    ));
  }

  getDepositos(): Observable<any> {
    return this.http.get(environment.apiUrl + 'depositos');
  }

  getArticulo(id: number): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'articles/' + id);
  }

  getProveedorByArticulo(id: number): Observable<any> {
    return this.http.get(environment.apiUrl + 'article-supplier/by-article/' + id);
  }

  getStocks(): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'stocks');
  }

  getArticuloByNombre(nombre: string): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'stocks/articulo/nombre/' + nombre);
  }

  getArticuloByDroga(nombre: string): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'stocks/articulo/droga/' + nombre);
  }

  getArticuloBycodeBarras(code: string): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/barcode/' + code);
  }

  getStockById(id): Observable<any> {
    return this.http.get(environment.apiUrl + 'stock/' + id);
  }

  getStockByVariantId(id): Observable<any> {
    return this.http.get(environment.apiUrl + 'stock/variant/' + id);
  }

  getStockByArticulo(id): Observable<any> {
    return this.http.get(environment.apiUrl + 'stock/articulo/' + id);
  }

  getArticleWithoutVariantId(id): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/article/without-variants/' + id);
  }

  actualizarStock(stock: any, id): Observable<string | any> {
    return this.http.patch(environment.apiUrl + 'stock/' + id, stock);
  }

  actualizarPreciosByArticulo(articulo: any): Observable<string | any> {
    return this.http.patch(environment.apiUrl + 'articles/' + articulo.id, articulo);
  }

  /**
   * Obtiene previsualización de actualización masiva de precios
   * @param payload - Configuración de filtros y método de actualización
   * @returns Observable con metadata y previewItems calculados en el backend
   */
  obtenerPreviewPreciosMasivos(payload: any): Observable<any> {
    console.log('🔍 Llamando a preview:', environment.apiUrl + 'articles/prices/preview');
    console.log('📦 Con payload:', payload);
    return this.http.post(environment.apiUrl + 'articles/prices/preview', payload);
  }

  /**
   * Actualiza precios de forma masiva usando el endpoint bulk-update-prices
   * @param payload - BulkUpdatePricesAdvancedDto: { updateMethod, adjustmentValue, adjustmentType?, filters, metadata }
   * @returns Observable<BulkUpdateAdvancedResult> con el resultado de la operación
   */
  actualizarPreciosMasivos(payload: any): Observable<any> {
    console.log('🔗 Llamando a:', environment.apiUrl + 'articles/bulk-update-prices');
    console.log('📦 Con payload:', payload);
    return this.http.post(environment.apiUrl + 'articles/bulk-update-prices', payload);
  }

  /**
   * Ejecuta la actualización masiva de precios usando el endpoint /api/articles/prices/execute
   * @param payload - Datos para la ejecución de la actualización masiva
   * @returns Observable con el resultado de la operación
   */
  ejecutarActualizacionMasiva(payload: any): Observable<any> {
    console.log('🔗 Ejecutando actualización masiva:', environment.apiUrl + 'articles/prices/execute');
    console.log('📦 Con payload:', payload);
    return this.http.post(environment.apiUrl + 'articles/prices/execute', payload);
  }

  actualizarCantidadStock(cantidad: any, id): Observable<string | any> {
    return this.http.patch(environment.apiUrl + 'stock/cantidadarticulo/' + id, {
      cantidad: cantidad,
    });
  }

  getArticuloPorcode(code: string): Observable<Product | any> {
    return (this.articulo = this.http.get(environment.apiUrl + 'articles/code/' + code));
  }

  getArticuloPorNombre(nombre: string): Observable<Product | any> {
    return (this.articulo = this.http.get(
      environment.apiUrl + 'articles/nombre/' + nombre
    ));
  }

  guardarArticulo(articulo: Product): Observable<any> {
    return this.http.post(environment.apiUrl + 'articles', articulo);
  }

  modificarArticulo(articulo: Product): Observable<any> {
    return this.http.patch(environment.apiUrl + 'articles/' + articulo.id, articulo);
  }

  eliminarArticulo(id: number): Observable<string | any> {
    return this.http.delete(environment.apiUrl + 'articles/' + id);
  }

  eliminarStock(id: number): Observable<string | any> {
    return this.http.delete(environment.apiUrl + 'stock/' + id);
  }

  getProductosSinStock(): Observable<Product | any> {
    return this.http.get(environment.apiUrl + 'reportes/stock/sin-stock');
  }

  // VARIANTES

  getVariantesPorArticulo(idArticulo: number): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant/article/' + idArticulo);
  }

  // Método para obtener una variante por su ID
  getVarianteById(id: number): Observable<Variant> {
    return this.http.get<Variant>(`${environment.apiUrl}variant/${id}`);
  }

  guardarVariante(dataVariante: Variant): Observable<any> {
    return this.http.post(environment.apiUrl + 'variant', dataVariante);
  }

  modificarVariante(dataVariante: Variant): Observable<any> {
    return this.http.patch(
      environment.apiUrl + 'variant/' + dataVariante.id,
      dataVariante
    );
  }

  deleteVariante(idVariante: number): Observable<any> {
    return this.http.delete(environment.apiUrl + 'variant/' + idVariante);
  }

  // type DE VARIANTES
  gettypeVariantes(): Observable<any> {
    return this.http.get(environment.apiUrl + 'variant-type');
  }

  // Conversion Units
  getConversionUnits(articleId: number): Observable<ConversionUnits[]> {
    return this.http.get<ConversionUnits[]>(
      `${environment.apiUrl}conversion-unit/article/${articleId}`
    );
  }

  createConversionUnit(
    articleId: number,
    data: Partial<ConversionUnits>
  ): Observable<ConversionUnits> {
    return this.http.post<ConversionUnits>(`${environment.apiUrl}conversion-unit`, {
      ...data,
      articleId,
    });
  }

  updateConversionUnit(
    id: number,
    data: Partial<ConversionUnits>
  ): Observable<ConversionUnits> {
    return this.http.patch<ConversionUnits>(
      `${environment.apiUrl}conversion-unit/${id}`,
      data
    );
  }

  deleteConversionUnit(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}conversion-unit/${id}`);
  }

  // Composite Products Methods
  getCompositeArticleVariants(articleId: number): Observable<CompositeArticleVariant[]> {
    return this.http.get<CompositeArticleVariant[]>(
      `${environment.apiUrl}composite-article-variants/article/${articleId}`
    );
  }

  addCompositeArticleVariant(
    data: CreateCompositeArticleVariantDto
  ): Observable<CompositeArticleVariant> {
    return this.http.post<CompositeArticleVariant>(
      `${environment.apiUrl}composite-article-variants`,
      data
    );
  }

  updateCompositeArticleVariant(
    id: number,
    data: Partial<CreateCompositeArticleVariantDto>
  ): Observable<CompositeArticleVariant> {
    return this.http.patch<CompositeArticleVariant>(
      `${environment.apiUrl}composite-article-variants/${id}`,
      data
    );
  }

  deleteCompositeArticleVariant(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}composite-article-variants/${id}`);
  }

  // ===== MÉTODOS PARA GESTIÓN DE IMPUESTOS DE ARTÍCULOS =====

  /**
   * Obtiene todos los impuestos de un artículo
   */
  getArticleTaxes(articleId: number): Observable<ArticleTax[]> {
    return this.http.get<ArticleTax[]>(`${environment.apiUrl}article-tax/article/${articleId}`);
  }

  /**
   * Agrega un nuevo impuesto a un artículo
   */
  addArticleTax(data: CreateArticleTaxDto): Observable<ArticleTax> {
    return this.http.post<ArticleTax>(`${environment.apiUrl}article-tax`, data);
  }

  /**
   * Actualiza un impuesto de artículo existente
   */
  updateArticleTax(id: number, data: UpdateArticleTaxDto): Observable<ArticleTax> {
    return this.http.patch<ArticleTax>(`${environment.apiUrl}article-tax/${id}`, data);
  }

  /**
   * Elimina un impuesto de un artículo
   */
  deleteArticleTax(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}article-tax/${id}`);
  }

  /**
   * Elimina todos los impuestos de un artículo
   */
  deleteAllArticleTaxes(articleId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}article-tax/article/${articleId}`);
  }
}
